import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Hammer,
  Briefcase,
  Users,
  FileArchive,
  LayoutDashboard,
  Plus,
  Loader,
  AlertCircle,
  Tag,
  UserPlus,
  Search,
  Download,
  Upload,
  Pencil,
  Trash2,
  Clock,
  CalendarClock,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { useIsMobile } from '../hooks/useIsMobile';
import { cn } from '../utils/cn';
import { buildTeamApiService } from '../services/buildTeamApi';
import { usernameCacheService } from '../services/usernameCache';
import {
  BuildProject,
  BuildItem,
  BuildTeamMember,
  BuildSchematic,
  BuildTag,
  BuildDashboardStats,
  BuildActivityLogEntry,
  ProjectListParams,
  BuildItemListParams,
  TeamMemberRole,
  TeamMemberStatus,
  BUILD_PROJECT_STATUSES,
  BUILD_ITEM_STATUSES,
  TEAM_MEMBER_ROLES,
  TEAM_MEMBER_STATUSES,
} from '../types/buildTeam';
import { BuildStatsCards } from '../components/buildteam/BuildStatsCards';
import { BuildStatusBadge } from '../components/buildteam/BuildStatusBadge';
import { BuildPriorityBadge } from '../components/buildteam/BuildPriorityBadge';
import { BuildFilters } from '../components/buildteam/BuildFilters';
import { PlayerProfile } from '../components/PlayerProfile';
import { CreatorProfile } from '../components/CreatorProfile';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { CreateProjectModal } from '../components/buildteam/CreateProjectModal';
import { CreateBuildItemModal } from '../components/buildteam/CreateBuildItemModal';
import { CreateTeamMemberModal } from '../components/buildteam/CreateTeamMemberModal';
import { SchematicEditForm, SchematicScreenshotPreview, SchematicUploadForm, schematicKindLabel } from '../components/buildteam/BuildSchematicsSection';
import { SchematicDeliveryAction } from '../components/buildteam/SchematicDeliveryAction';
import { schematicDisplayTitle, schematicShowsFilename } from '../utils/buildSchematic';
import { EditTeamMemberModal } from '../components/buildteam/EditTeamMemberModal';
import { BuildTagManagementModal } from '../components/buildteam/BuildTagManagementModal';
import { formatDistanceToNow, isPast, differenceInDays } from 'date-fns';
import { getLimitedTesseractTeamAccess } from '../utils/tesseractTeamAccess';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { BuilderContributions } from '../components/buildteam/BuilderContributions';
import { StatTile } from '../components/ui/StatTile';
import { PanelTour } from '../components/ui/PanelTour';
import { buildPanelTour } from '../config/panelTours';
import { useTourNavigation } from '../hooks/useTourNavigation';

type ViewMode = 'dashboard' | 'projects' | 'builds' | 'team' | 'schematics';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function parseSpecialties(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parseDimensions(raw: string | null): { x: string; y: string; z: string } | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** API may return Unix seconds or epoch milliseconds (see BuildActivityLog). */
function toEpochMs(ts: number): number {
  return ts < 1e12 ? ts * 1000 : ts;
}

function getDueDateStyle(dueDateUnix: number, status?: string): { className: string; label: string } {
  // Completed/cancelled work is never "overdue" or "due soon" — show the date neutrally.
  if (status === 'completed' || status === 'cancelled') {
    return { className: 'text-glass-400', label: '' };
  }
  const dueDate = new Date(toEpochMs(dueDateUnix));
  const now = new Date();
  if (isPast(dueDate)) {
    return { className: 'text-red-400', label: 'Overdue' };
  }
  const daysLeft = differenceInDays(dueDate, now);
  if (daysLeft <= 3) {
    return { className: 'text-amber-400', label: 'Due soon' };
  }
  return { className: 'text-glass-400', label: '' };
}

const listItemVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const VIEW_MODES: { mode: ViewMode; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { mode: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { mode: 'projects', label: 'Projects', icon: Briefcase },
  { mode: 'builds', label: 'Builds', icon: Hammer },
  { mode: 'team', label: 'Team', icon: Users },
  { mode: 'schematics', label: 'Schematics', icon: FileArchive },
];

export const BuildTeamView = () => {
  const { identity, isLoading: authLoading } = useAuth();
  const { tourNavigation } = useTourNavigation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  useDocumentTitle('Build Team');

  const isDev = import.meta.env.DEV;

  // ── Permissions ──────────────────────────────────────────────────────────

  const limitedTesseract = useMemo(
    () => getLimitedTesseractTeamAccess(identity, isDev),
    [identity, isDev],
  );

  const canView = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    if (
      limitedTesseract &&
      !limitedTesseract.calendarViewOnly &&
      limitedTesseract.role === 'buildteam'
    ) {
      return true;
    }
    const groups = identity.raw.custom.groups;
    return (
      groups.includes('argus_build_panel') ||
      groups.includes('argus_build_lead') ||
      groups.includes('argus_build_admin') ||
      groups.includes('SPY_Super_admin')
    );
  }, [identity, isDev, limitedTesseract]);

  const canCreate = useMemo(() => {
    if (isDev) return true;
    if (
      limitedTesseract &&
      !limitedTesseract.calendarViewOnly &&
      limitedTesseract.role === 'buildteam'
    ) {
      return false;
    }
    return canView;
  }, [isDev, limitedTesseract, canView]);

  const canDelete = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_build_admin') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const canAdmin = useMemo(() => canDelete, [canDelete]);

  const canLead = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return (
      groups.includes('argus_build_lead') ||
      groups.includes('argus_build_admin') ||
      groups.includes('SPY_Super_admin')
    );
  }, [identity, isDev]);
  void canLead;

  // ── View mode ────────────────────────────────────────────────────────────

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('tab');
  const viewMode: ViewMode = VIEW_MODES.find(v => v.mode === requestedView)?.mode ?? 'dashboard';
  const setViewMode = (mode: ViewMode) => setSearchParams({ tab: mode });
  const expandedMember = searchParams.get('member');
  const setExpandedMember = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('member', id); else next.delete('member');
    setSearchParams(next, { replace: true });
  };

  // ── Dashboard state ──────────────────────────────────────────────────────

  const [stats, setStats] = useState<BuildDashboardStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<BuildActivityLogEntry[]>([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  // ── Tags (shared) ────────────────────────────────────────────────────────

  const [tags, setTags] = useState<BuildTag[]>([]);

  // ── Projects state ───────────────────────────────────────────────────────

  const [projects, setProjects] = useState<BuildProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [projectsTotal, setProjectsTotal] = useState(0);
  const [projectsPage, setProjectsPage] = useState(1);
  const PAGE_SIZE = 50;

  // Project filters
  const [projectSearch, setProjectSearch] = useState('');
  const [projectStatus, setProjectStatus] = useState('');
  const [projectCategory, setProjectCategory] = useState('');
  const [projectTagFilter, setProjectTagFilter] = useState('');
  const [projectPriorityMin, setProjectPriorityMin] = useState('');
  const [projectPriorityMax, setProjectPriorityMax] = useState('');
  const [projectSortBy, setProjectSortBy] = useState<string>('created_at');
  const [projectSortOrder, setProjectSortOrder] = useState<'asc' | 'desc'>('desc');

  // ── Builds state ─────────────────────────────────────────────────────────

  const [builds, setBuilds] = useState<BuildItem[]>([]);
  const [buildsLoading, setBuildsLoading] = useState(false);
  const [buildsError, setBuildsError] = useState<string | null>(null);
  const [buildsTotal, setBuildsTotal] = useState(0);
  const [buildsPage, setBuildsPage] = useState(1);

  // Build filters
  const [buildSearch, setBuildSearch] = useState('');
  const [buildStatus, setBuildStatus] = useState('');
  const [buildCategory, setBuildCategory] = useState('');
  const [buildTagFilter, setBuildTagFilter] = useState('');
  const [buildPriorityMin, setBuildPriorityMin] = useState('');
  const [buildPriorityMax, setBuildPriorityMax] = useState('');
  const [buildSortBy, setBuildSortBy] = useState<string>('created_at');
  const [buildSortOrder, setBuildSortOrder] = useState<'asc' | 'desc'>('desc');

  // ── Team state ───────────────────────────────────────────────────────────

  const [teamMembers, setTeamMembers] = useState<BuildTeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [teamTotal, setTeamTotal] = useState(0);
  const [teamPage, setTeamPage] = useState(1);
  const teamSearch = searchParams.get('q') ?? '';
  const [teamRoleFilter, setTeamRoleFilter] = useState<TeamMemberRole | ''>('');
  const [teamStatusFilter, setTeamStatusFilter] = useState<TeamMemberStatus | ''>('');
  const [editingMember, setEditingMember] = useState<BuildTeamMember | null>(null);
  const [memberToDelete, setMemberToDelete] = useState<BuildTeamMember | null>(null);
  const [isDeletingMember, setIsDeletingMember] = useState(false);

  // ── Schematics state ────────────────────────────────────────────────────

  const [schematics, setSchematics] = useState<BuildSchematic[]>([]);
  const [schematicsLoading, setSchematicsLoading] = useState(false);
  const [schematicsError, setSchematicsError] = useState<string | null>(null);
  const [schematicsPage, setSchematicsPage] = useState(1);
  const [schematicsTotal, setSchematicsTotal] = useState(0);
  const [schematicsSearch, setSchematicsSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // ── Modals ───────────────────────────────────────────────────────────────

  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
  const [isCreateBuildOpen, setIsCreateBuildOpen] = useState(false);
  const [isCreateMemberOpen, setIsCreateMemberOpen] = useState(false);
  const [isTagManagementOpen, setIsTagManagementOpen] = useState(false);
  const [showSchematicUpload, setShowSchematicUpload] = useState(false);
  const [editingSchematicId, setEditingSchematicId] = useState<string | null>(null);
  const [confirmDeleteSchematicId, setConfirmDeleteSchematicId] = useState<string | null>(null);
  const [deletingSchematicId, setDeletingSchematicId] = useState<string | null>(null);

  // ── Debounced searches ───────────────────────────────────────────────────

  const debouncedProjectSearch = useDebounce(projectSearch, 400);
  const debouncedBuildSearch = useDebounce(buildSearch, 400);
  const debouncedTeamSearch = useDebounce(teamSearch, 400);
  const debouncedSchematicsSearch = useDebounce(schematicsSearch, 400);

  // ── Load tags ────────────────────────────────────────────────────────────

  useEffect(() => {
    buildTeamApiService.listTags()
      .then(setTags)
      .catch((err) => {
        console.error('Failed to load tags:', err);
      });
  }, []);

  // ── Dashboard loaders ────────────────────────────────────────────────────

  const loadStats = useCallback(async () => {
    try {
      const s = await buildTeamApiService.getProjectStats();
      setStats(s);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const [statsData, activityData] = await Promise.all([
        buildTeamApiService.getProjectStats(),
        buildTeamApiService.getRecentActivity(20),
      ]);
      setStats(statsData);
      setRecentActivity(activityData);

      const uuids = new Set<string>();
      activityData.forEach(a => { if (a.actor_id) uuids.add(a.actor_id); });
      if (uuids.size > 0) usernameCacheService.prefetchUsernames(Array.from(uuids));
    } catch (err) {
      setDashboardError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === 'dashboard') loadDashboard();
  }, [loadDashboard, viewMode]);

  // ── Projects loader ──────────────────────────────────────────────────────

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const params: ProjectListParams = {
        page: projectsPage,
        limit: PAGE_SIZE,
        sort_by: projectSortBy as ProjectListParams['sort_by'],
        sort_order: projectSortOrder,
      };
      if (debouncedProjectSearch) params.search = debouncedProjectSearch;
      if (projectStatus) params.status = projectStatus as ProjectListParams['status'];
      if (projectCategory) params.category = projectCategory as ProjectListParams['category'];
      if (projectTagFilter) params.tag_id = projectTagFilter;
      if (projectPriorityMin) params.priority_min = Number(projectPriorityMin);
      if (projectPriorityMax) params.priority_max = Number(projectPriorityMax);

      const response = await buildTeamApiService.listProjects(params);
      if (Array.isArray(response)) {
        setProjects(response);
        setProjectsTotal(response.length);
      } else {
        setProjects(response.results || []);
        setProjectsTotal(response.total || 0);
      }

      const uuids = new Set<string>();
      const projectsList = Array.isArray(response) ? response : response.results || [];
      projectsList.forEach(p => { if (p.created_by) uuids.add(p.created_by); });
      if (uuids.size > 0) usernameCacheService.prefetchUsernames(Array.from(uuids));
    } catch (err) {
      setProjectsError(err instanceof Error ? err.message : 'Failed to load projects');
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  }, [projectsPage, projectSortBy, projectSortOrder, debouncedProjectSearch, projectStatus, projectCategory, projectTagFilter, projectPriorityMin, projectPriorityMax]);

  useEffect(() => {
    if (viewMode === 'projects') loadProjects();
  }, [loadProjects, viewMode]);

  // ── Builds loader ────────────────────────────────────────────────────────

  const loadBuilds = useCallback(async () => {
    setBuildsLoading(true);
    setBuildsError(null);
    try {
      const params: BuildItemListParams = {
        page: buildsPage,
        limit: PAGE_SIZE,
        sort_by: buildSortBy as BuildItemListParams['sort_by'],
        sort_order: buildSortOrder,
      };
      if (debouncedBuildSearch) params.search = debouncedBuildSearch;
      if (buildStatus) params.status = buildStatus as BuildItemListParams['status'];
      if (buildCategory) params.category = buildCategory;
      if (buildTagFilter) params.tag_id = buildTagFilter;
      if (buildPriorityMin) params.priority_min = Number(buildPriorityMin);
      if (buildPriorityMax) params.priority_max = Number(buildPriorityMax);

      const response = await buildTeamApiService.listBuildItems(params);
      if (Array.isArray(response)) {
        setBuilds(response);
        setBuildsTotal(response.length);
      } else {
        setBuilds(response.results || []);
        setBuildsTotal(response.total || 0);
      }

      const uuids = new Set<string>();
      const buildsList = Array.isArray(response) ? response : response.results || [];
      buildsList.forEach(b => { if (b.created_by) uuids.add(b.created_by); });
      if (uuids.size > 0) usernameCacheService.prefetchUsernames(Array.from(uuids));
    } catch (err) {
      setBuildsError(err instanceof Error ? err.message : 'Failed to load builds');
      setBuilds([]);
    } finally {
      setBuildsLoading(false);
    }
  }, [buildsPage, buildSortBy, buildSortOrder, debouncedBuildSearch, buildStatus, buildCategory, buildTagFilter, buildPriorityMin, buildPriorityMax]);

  useEffect(() => {
    if (viewMode === 'builds') loadBuilds();
  }, [loadBuilds, viewMode]);

  // ── Team loader ──────────────────────────────────────────────────────────

  const loadTeam = useCallback(async () => {
    setTeamLoading(true);
    setTeamError(null);
    try {
      if (debouncedTeamSearch) {
        const results = await buildTeamApiService.searchTeamMembers(debouncedTeamSearch);
        let filtered = results;
        if (teamRoleFilter) filtered = filtered.filter(m => m.role === teamRoleFilter);
        if (teamStatusFilter) filtered = filtered.filter(m => m.status === teamStatusFilter);
        setTeamMembers(filtered);
        setTeamTotal(filtered.length);
      } else {
        const response = await buildTeamApiService.listTeamMembers({
          role: teamRoleFilter || undefined,
          status: teamStatusFilter || undefined,
          page: teamPage,
          limit: PAGE_SIZE,
        });
        if (Array.isArray(response)) {
          setTeamMembers(response);
          setTeamTotal(response.length);
        } else {
          setTeamMembers(response.results || []);
          setTeamTotal(response.total || 0);
        }
      }
    } catch (err) {
      setTeamError(err instanceof Error ? err.message : 'Failed to load team members');
      setTeamMembers([]);
    } finally {
      setTeamLoading(false);
    }
  }, [teamPage, debouncedTeamSearch, teamRoleFilter, teamStatusFilter]);

  useEffect(() => {
    if (viewMode === 'team') loadTeam();
  }, [loadTeam, viewMode]);

  // ── Schematics loader ───────────────────────────────────────────────────

  const loadSchematics = useCallback(async () => {
    setSchematicsLoading(true);
    setSchematicsError(null);
    try {
      if (debouncedSchematicsSearch) {
        const results = await buildTeamApiService.searchSchematics(debouncedSchematicsSearch);
        setSchematics(results);
        setSchematicsTotal(results.length);
      } else {
        const results = await buildTeamApiService.listSchematics(schematicsPage, PAGE_SIZE);
        setSchematics(results);
        setSchematicsTotal(results.length);
      }
    } catch (err) {
      setSchematicsError(err instanceof Error ? err.message : 'Failed to load schematics');
      setSchematics([]);
    } finally {
      setSchematicsLoading(false);
    }
  }, [schematicsPage, debouncedSchematicsSearch]);

  useEffect(() => {
    if (viewMode === 'schematics') loadSchematics();
  }, [loadSchematics, viewMode]);

  // ── Page resets on filter change ─────────────────────────────────────────

  useEffect(() => {
    setProjectsPage(1);
  }, [debouncedProjectSearch, projectStatus, projectCategory, projectTagFilter, projectPriorityMin, projectPriorityMax]);

  useEffect(() => {
    setBuildsPage(1);
  }, [debouncedBuildSearch, buildStatus, buildCategory, buildTagFilter, buildPriorityMin, buildPriorityMax]);

  useEffect(() => {
    setTeamPage(1);
  }, [debouncedTeamSearch, teamRoleFilter, teamStatusFilter]);

  useEffect(() => {
    setSchematicsPage(1);
  }, [debouncedSchematicsSearch]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleRefresh = () => {
    loadStats();
    buildTeamApiService.listTags().then(setTags).catch(() => {});
    if (viewMode === 'dashboard') loadDashboard();
    else if (viewMode === 'projects') loadProjects();
    else if (viewMode === 'builds') loadBuilds();
    else if (viewMode === 'team') loadTeam();
    else if (viewMode === 'schematics') loadSchematics();
  };

  const handleDeleteSchematic = async () => {
    if (!confirmDeleteSchematicId || deletingSchematicId) return;
    setDeletingSchematicId(confirmDeleteSchematicId);
    try {
      await buildTeamApiService.deleteSchematic(confirmDeleteSchematicId);
      setConfirmDeleteSchematicId(null);
      await loadSchematics();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete file');
    } finally {
      setDeletingSchematicId(null);
    }
  };

  const handleDownloadSchematic = async (schematic: BuildSchematic) => {
    setDownloadingId(schematic.id);
    try {
      const response = await buildTeamApiService.downloadSchematic(schematic.id);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = schematic.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteMember = async (member: BuildTeamMember) => {
    setMemberToDelete(member);
  };

  const confirmDeleteMember = async () => {
    if (!memberToDelete) return;
    setIsDeletingMember(true);
    try {
      await buildTeamApiService.deleteTeamMember(memberToDelete.id);
      setMemberToDelete(null);
      loadTeam();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove team member');
    } finally {
      setIsDeletingMember(false);
    }
  };

  // ── Computed pagination ──────────────────────────────────────────────────

  const projectsTotalPages = Math.ceil(projectsTotal / PAGE_SIZE);
  const buildsTotalPages = Math.ceil(buildsTotal / PAGE_SIZE);
  const teamTotalPages = Math.ceil(teamTotal / PAGE_SIZE);
  const schematicsTotalPages = Math.ceil(schematicsTotal / PAGE_SIZE);

  // ── Role badge config ────────────────────────────────────────────────────

  const roleBadgeConfig: Record<string, string> = {
    builder: 'badge-info',
    lead: 'badge-purple',
    manager: 'badge-amber',
    admin: 'badge-danger',
  };

  const memberStatusConfig: Record<string, string> = {
    active: 'badge-success',
    inactive: 'badge-neutral',
    on_leave: 'badge-warning',
  };

  // ── Derived display state ────────────────────────────────────────────────

  const projectsFiltered = Boolean(debouncedProjectSearch || projectStatus || projectCategory || projectTagFilter);
  const buildsFiltered = Boolean(debouncedBuildSearch || buildStatus || buildCategory || buildTagFilter);
  const teamFiltered = Boolean(debouncedTeamSearch || teamRoleFilter || teamStatusFilter);

  const resultCount = (() => {
    if (viewMode === 'projects') return projectsLoading ? null : projectsTotal;
    if (viewMode === 'builds') return buildsLoading ? null : buildsTotal;
    if (viewMode === 'team') return teamLoading ? null : teamTotal;
    if (viewMode === 'schematics') return schematicsLoading ? null : schematicsTotal;
    return null;
  })();

  const renderError = (message: string) => (
    <div className="alert alert-danger" role="alert">
      <AlertCircle />
      <span>{message}</span>
    </div>
  );

  /** Layout-mirroring skeleton for the card lists. */
  const renderListSkeleton = (rows = 4) => (
    <div className="space-y-3" aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="glass-card p-4 flex items-start gap-4">
          <SkeletonLoader className="w-9 h-9 rounded-lg shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <SkeletonLoader className="h-4 w-48 max-w-full" />
            <div className="flex gap-2">
              <SkeletonLoader className="h-5 w-20 rounded-full" />
              <SkeletonLoader className="h-5 w-10 rounded-full" />
            </div>
            <SkeletonLoader className="h-3 w-full max-w-md" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderPagination = (
    current: number,
    pages: number,
    total: number,
    onPrev: () => void,
    onNext: () => void,
  ) => (
    <div className="glass-card p-3 flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="text-xs text-glass-400 tabular-nums">
        Showing {((current - 1) * PAGE_SIZE) + 1}–{Math.min(current * PAGE_SIZE, total)} of {total.toLocaleString()}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={current <= 1}
          className="glass-button-secondary touch-manipulation"
        >
          Previous
        </button>
        <span className="text-xs text-glass-400 tabular-nums whitespace-nowrap">
          Page {current} of {pages}
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={current >= pages}
          className="glass-button-secondary touch-manipulation"
        >
          Next
        </button>
      </div>
    </div>
  );

  if (!canView) {
    return (
      <EmptyState
        tone="danger"
        icon={AlertCircle}
        title="Access Denied"
        description="You do not have permission to view the Build Team panel."
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ────────────────────────────────────────────────────── */}
      <PageHeader
        eyebrow="Build Team"
        title="Build Team"
        description="Follow the work, find a builder’s contributions, and keep every file connected."
        icon={Hammer}
        actions={
          <>
            <PanelTour tourId="build-team" title="Build Team" steps={buildPanelTour(canCreate, canAdmin)} enabled={canView && !authLoading} {...tourNavigation} />
            {canAdmin && (viewMode === 'team' || viewMode === 'dashboard') && (
              <>
                <button
                  onClick={() => setIsTagManagementOpen(true)}
                  className="glass-button-secondary"
                  title="Manage Tags"
                  aria-label="Manage Tags"
                >
                  <Tag className="w-4 h-4" />
                  <span className={cn(isMobile && 'hidden')}>Manage Tags</span>
                </button>
                <button
                  onClick={() => setIsCreateMemberOpen(true)}
                  data-tour="build-add-member"
                  className="glass-button-secondary"
                  title="Add Member"
                  aria-label="Add Member"
                >
                  <UserPlus className="w-4 h-4" />
                  <span className={cn(isMobile && 'hidden')}>Add Member</span>
                </button>
              </>
            )}
            {canCreate && (viewMode === 'projects' || viewMode === 'builds' || viewMode === 'dashboard') && (
              <>
                <button
                  onClick={() => setIsCreateBuildOpen(true)}
                  className="glass-button"
                  title="New Build"
                  aria-label="New Build"
                >
                  <Hammer className="w-4 h-4" />
                  <span className={cn(isMobile && 'hidden')}>New Build</span>
                </button>
                <button
                  onClick={() => setIsCreateProjectOpen(true)}
                  data-tour="build-create"
                  className="btn-primary"
                >
                  <Plus className="w-4 h-4" />
                  New Project
                </button>
              </>
            )}
          </>
        }
      >
        {/* ── View Mode Toggle + result count ───────────────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div data-tour="build-navigation" className="segmented flex-wrap max-w-full" role="tablist" aria-label="Build Team views">
            {VIEW_MODES.map(({ mode, label, icon: Icon }) => (
              <button
                key={mode}
                data-tour={`build-${mode}`}
                type="button"
                role="tab"
                aria-selected={viewMode === mode}
                onClick={() => setViewMode(mode)}
                className="segmented-item touch-manipulation"
                data-active={viewMode === mode ? 'true' : 'false'}
                title={label}
                aria-label={label}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>
          {resultCount !== null && (
            <span className="text-xs text-glass-400 tabular-nums">
              {resultCount.toLocaleString()} {resultCount === 1 ? 'result' : 'results'}
            </span>
          )}
        </div>

        {/* ── Filters (projects) ────────────────────────────────────── */}
        {viewMode === 'projects' && (
          <BuildFilters
            searchQuery={projectSearch}
            onSearchChange={setProjectSearch}
            statusFilter={projectStatus}
            onStatusChange={setProjectStatus}
            categoryFilter={projectCategory}
            onCategoryChange={setProjectCategory}
            tags={tags}
            tagFilter={projectTagFilter}
            onTagChange={setProjectTagFilter}
            priorityMin={projectPriorityMin}
            onPriorityMinChange={setProjectPriorityMin}
            priorityMax={projectPriorityMax}
            onPriorityMaxChange={setProjectPriorityMax}
            sortBy={projectSortBy}
            onSortByChange={setProjectSortBy}
            sortOrder={projectSortOrder}
            onSortOrderChange={setProjectSortOrder}
            statuses={BUILD_PROJECT_STATUSES}
          />
        )}

        {/* ── Filters (builds) ──────────────────────────────────────── */}
        {viewMode === 'builds' && (
          <BuildFilters
            searchQuery={buildSearch}
            onSearchChange={setBuildSearch}
            statusFilter={buildStatus}
            onStatusChange={setBuildStatus}
            categoryFilter={buildCategory}
            onCategoryChange={setBuildCategory}
            tags={tags}
            tagFilter={buildTagFilter}
            onTagChange={setBuildTagFilter}
            priorityMin={buildPriorityMin}
            onPriorityMinChange={setBuildPriorityMin}
            priorityMax={buildPriorityMax}
            onPriorityMaxChange={setBuildPriorityMax}
            sortBy={buildSortBy}
            onSortByChange={setBuildSortBy}
            sortOrder={buildSortOrder}
            onSortOrderChange={setBuildSortOrder}
            statuses={BUILD_ITEM_STATUSES}
          />
        )}

        {/* ── Filters (team) ────────────────────────────────────────── */}
        {viewMode === 'team' && (
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
              <input
                type="text"
                value={teamSearch}
                data-tour="build-people-search"
                onChange={(e) => {
                  const next = new URLSearchParams(searchParams);
                  if (e.target.value) next.set('q', e.target.value); else next.delete('q');
                  setSearchParams(next, { replace: true });
                }}
                className="glass-input w-full pl-9"
                placeholder="Search team members…"
              />
            </div>
            <select
              value={teamRoleFilter}
              onChange={(e) => setTeamRoleFilter(e.target.value as TeamMemberRole | '')}
              className="glass-select md:w-40"
              aria-label="Filter by role"
            >
              <option value="">All Roles</option>
              {TEAM_MEMBER_ROLES.map((r) => (
                <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
              ))}
            </select>
            <select
              value={teamStatusFilter}
              onChange={(e) => setTeamStatusFilter(e.target.value as TeamMemberStatus | '')}
              className="glass-select md:w-44"
              aria-label="Filter by status"
            >
              <option value="">All Statuses</option>
              {TEAM_MEMBER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* ── Filters (schematics) ──────────────────────────────────── */}
        {viewMode === 'schematics' && (
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
              <input
                type="text"
                value={schematicsSearch}
                data-tour="build-files-search"
                onChange={(e) => setSchematicsSearch(e.target.value)}
                className="glass-input w-full pl-9"
                placeholder="Search by title, filename or description…"
              />
            </div>
            {canCreate && (
              <button
                onClick={() => setShowSchematicUpload(!showSchematicUpload)}
                data-tour="build-files-upload"
                className={cn('glass-button', showSchematicUpload && 'border-accent-500/40')}
                aria-expanded={showSchematicUpload}
              >
                <Upload className="w-4 h-4" />
                Upload
              </button>
            )}
          </div>
        )}
      </PageHeader>

      {/* ── Dashboard Mode ──────────────────────────────────────────── */}
      {viewMode === 'dashboard' && (
        <>
          <div className="grid md:grid-cols-3 gap-3">
            <StatTile label="Who worked on what?" value="Find a builder" hint="Projects, assignments and schematics together" icon={Users} tone="accent" onClick={() => setViewMode('team')} />
            <StatTile label="Follow the work" value="Browse projects" hint="See progress and open assignments" icon={Briefcase} onClick={() => setViewMode('projects')} />
            <StatTile label="Find a file" value="Schematics" hint="Search the shared file library" icon={FileArchive} onClick={() => setViewMode('schematics')} />
          </div>
          {dashboardError && renderError(dashboardError)}

          <BuildStatsCards stats={stats} loading={dashboardLoading} />

          {dashboardLoading && !stats && (
            <div className="glass-card p-4 md:p-5" aria-busy="true">
              <SkeletonLoader className="h-4 w-32 mb-4" />
              <div className="divide-y divide-glass-200/10">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3 py-3">
                    <div className="flex-1 space-y-2">
                      <SkeletonLoader className="h-4 w-3/4" />
                      <SkeletonLoader className="h-3 w-40" />
                    </div>
                    <SkeletonLoader className="h-5 w-16 rounded-md" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {recentActivity.length > 0 && (
            <div className="glass-card p-4 md:p-5">
              <SectionHeader title="Recent Activity" icon={Clock} count={recentActivity.length} />
              <div className="divide-y divide-glass-200/10">
                {recentActivity.map((entry) => (
                  <div key={entry.id} className="flex items-start gap-3 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-glass-200">{entry.description}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-glass-500">
                        <CreatorProfile creatorId={entry.actor_id} inline />
                        <span className="tabular-nums">
                          {formatDistanceToNow(new Date(toEpochMs(entry.created_at)), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                    <span className="chip shrink-0">{entry.target_type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!dashboardLoading && recentActivity.length === 0 && stats && (
            <EmptyState
              icon={Clock}
              title="No recent activity"
              description="Activity from projects, builds, and team changes will appear here."
            />
          )}
        </>
      )}

      {/* ── Projects Mode ───────────────────────────────────────────── */}
      {viewMode === 'projects' && (
        <>
          {projectsError && renderError(projectsError)}

          {projectsLoading && projects.length === 0 && renderListSkeleton()}

          {!projectsLoading && projects.length === 0 ? (
            <EmptyState
              icon={projectsFiltered ? Search : Briefcase}
              title={projectsFiltered ? 'No projects match your filters' : 'No projects yet'}
              description={
                projectsFiltered
                  ? 'Try adjusting your search or filter criteria.'
                  : canCreate
                    ? 'Click "New Project" to create the first one.'
                    : 'Projects will appear here once created.'
              }
              action={
                !projectsFiltered && canCreate ? (
                  <button onClick={() => setIsCreateProjectOpen(true)} className="glass-button">
                    <Plus className="w-4 h-4" />
                    New Project
                  </button>
                ) : undefined
              }
            />
          ) : (
            <>
              <AnimatePresence>
                <div className="space-y-3">
                  {projects.map((project, index) => {
                    const dueStyle = project.due_date ? getDueDateStyle(project.due_date, project.status) : null;
                    return (
                      <motion.div
                        key={project.id}
                        layout
                        variants={listItemVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.15, delay: index * 0.03 }}
                        onClick={() => navigate(`/build-team/projects/${project.id}`)}
                        className="glass-card glass-card-interactive p-4 group"
                      >
                        <div className="flex items-start gap-4">
                          <span className="icon-tile icon-tile-accent">
                            <Briefcase className="w-4 h-4" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="text-sm font-semibold text-white truncate group-hover:text-accent-300 transition-colors">
                                {project.title}
                              </h3>
                              <BuildStatusBadge status={project.status} type="project" />
                              <BuildPriorityBadge priority={project.priority} />
                              {project.category && (
                                <span className="badge badge-accent">
                                  {project.category.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center flex-wrap gap-3 text-xs text-glass-500 tabular-nums">
                              {project.due_date && dueStyle && (
                                <span className={cn('inline-flex items-center gap-1 font-medium', dueStyle.className)}>
                                  <CalendarClock className="w-3 h-3" />
                                  {dueStyle.label ? `${dueStyle.label} · ` : ''}
                                  Due {formatDistanceToNow(new Date(toEpochMs(project.due_date)), { addSuffix: true })}
                                </span>
                              )}
                              <span>
                                Created {formatDistanceToNow(new Date(toEpochMs(project.created_at)), { addSuffix: true })}
                              </span>
                              {project.created_by && (
                                <span className="inline-flex items-center gap-1">
                                  by <CreatorProfile creatorId={project.created_by} inline />
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </AnimatePresence>

              {projectsTotalPages > 1 && renderPagination(
                projectsPage,
                projectsTotalPages,
                projectsTotal,
                () => setProjectsPage(p => Math.max(1, p - 1)),
                () => setProjectsPage(p => Math.min(projectsTotalPages, p + 1)),
              )}
            </>
          )}
        </>
      )}

      {/* ── Builds Mode ─────────────────────────────────────────────── */}
      {viewMode === 'builds' && (
        <>
          {buildsError && renderError(buildsError)}

          {buildsLoading && builds.length === 0 && renderListSkeleton()}

          {!buildsLoading && builds.length === 0 ? (
            <EmptyState
              icon={buildsFiltered ? Search : Hammer}
              title={buildsFiltered ? 'No builds match your filters' : 'No builds yet'}
              description={
                buildsFiltered
                  ? 'Try adjusting your search or filter criteria.'
                  : canCreate
                    ? 'Click "New Build" to create the first one.'
                    : 'Builds will appear here once created.'
              }
              action={
                !buildsFiltered && canCreate ? (
                  <button onClick={() => setIsCreateBuildOpen(true)} className="glass-button">
                    <Hammer className="w-4 h-4" />
                    New Build
                  </button>
                ) : undefined
              }
            />
          ) : (
            <>
              <AnimatePresence>
                <div className="space-y-3">
                  {builds.map((build, index) => {
                    const dims = parseDimensions(build.dimensions);
                    const dueStyle = build.due_date ? getDueDateStyle(build.due_date, build.status) : null;
                    return (
                      <motion.div
                        key={build.id}
                        layout
                        variants={listItemVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.15, delay: index * 0.03 }}
                        onClick={() => navigate(`/build-team/builds/${build.id}`)}
                        className="glass-card glass-card-interactive p-4 group"
                      >
                        <div className="flex items-start gap-4">
                          <span className="icon-tile icon-tile-info">
                            <Hammer className="w-4 h-4" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="text-sm font-semibold text-white truncate group-hover:text-accent-300 transition-colors">
                                {build.title}
                              </h3>
                              <BuildStatusBadge status={build.status} type="build" />
                              <BuildPriorityBadge priority={build.priority} />
                              {build.category && (
                                <span className="badge badge-info">
                                  {build.category.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center flex-wrap gap-3 text-xs text-glass-500 tabular-nums">
                              {build.due_date && dueStyle && (
                                <span className={cn('inline-flex items-center gap-1 font-medium', dueStyle.className)}>
                                  <CalendarClock className="w-3 h-3" />
                                  {dueStyle.label ? `${dueStyle.label} · ` : ''}
                                  Due {formatDistanceToNow(new Date(toEpochMs(build.due_date)), { addSuffix: true })}
                                </span>
                              )}
                              {build.project_id && (
                                <span className="truncate max-w-[200px] font-mono text-accent-300/80">
                                  Project: {build.project_id.slice(0, 8)}…
                                </span>
                              )}
                              {dims && (
                                <span className="font-mono">{dims.x}×{dims.y}×{dims.z}</span>
                              )}
                              <span>
                                Created {formatDistanceToNow(new Date(toEpochMs(build.created_at)), { addSuffix: true })}
                              </span>
                              {build.created_by && (
                                <span className="inline-flex items-center gap-1">
                                  by <CreatorProfile creatorId={build.created_by} inline />
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </AnimatePresence>

              {buildsTotalPages > 1 && renderPagination(
                buildsPage,
                buildsTotalPages,
                buildsTotal,
                () => setBuildsPage(p => Math.max(1, p - 1)),
                () => setBuildsPage(p => Math.min(buildsTotalPages, p + 1)),
              )}
            </>
          )}
        </>
      )}

      {/* ── Team Mode ───────────────────────────────────────────────── */}
      {viewMode === 'team' && (
        <>
          <SectionHeader title="People & contributions" icon={Users} description="Search a builder, then open their work history to see projects, assignments and schematics together." />
          {teamError && renderError(teamError)}

          {teamLoading && teamMembers.length === 0 && renderListSkeleton()}

          {!teamLoading && teamMembers.length === 0 ? (
            <EmptyState
              icon={teamFiltered ? Search : Users}
              title={teamFiltered ? 'No team members match your filters' : 'No team members yet'}
              description={
                teamFiltered
                  ? 'Try adjusting your search or filter criteria.'
                  : canAdmin
                    ? 'Click "Add Member" to add the first one.'
                    : 'Team members will appear here once added.'
              }
              action={
                !teamFiltered && canAdmin ? (
                  <button onClick={() => setIsCreateMemberOpen(true)} className="glass-button">
                    <UserPlus className="w-4 h-4" />
                    Add Member
                  </button>
                ) : undefined
              }
            />
          ) : (
            <>
              <AnimatePresence>
                <div className="space-y-3">
                  {teamMembers.map((member, index) => {
                    const specialties = parseSpecialties(member.specialties);
                    const roleConfig = roleBadgeConfig[member.role] || roleBadgeConfig.builder;
                    const statusConfig = memberStatusConfig[member.status] || memberStatusConfig.active;

                    return (
                      <motion.div
                        key={member.id}
                        layout
                        variants={listItemVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.15, delay: index * 0.03 }}
                        className="glass-card p-4 group"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            {member.minecraft_uuid ? (
                              <Link to={`/player/${member.minecraft_uuid}`} aria-label={`Open ${member.display_name}'s player profile`}><img src={`${isDev ? 'https://spy.playcdu.co' : ''}/cdu-heads/avatar/${member.minecraft_uuid}/32.png`} alt="" className="w-10 h-10 rounded-lg" /></Link>
                            ) : (
                              <span className="icon-tile">
                                <Users className="w-4 h-4" />
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="text-sm font-semibold text-white truncate">
                                {member.minecraft_uuid ? (
                                  <PlayerProfile uuid={member.minecraft_uuid} name={member.display_name} inline showUuid={false} showAvatar={false} />
                                ) : (
                                  member.display_name
                                )}
                              </h3>
                              <span className={cn('badge', roleConfig)}>
                                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                              </span>
                              <span className={cn('badge badge-dot', statusConfig)}>
                                {member.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                              </span>
                            </div>

                            {specialties.length > 0 && (
                              <div className="flex items-center flex-wrap gap-1.5 mb-1">
                                {specialties.map((s, i) => (
                                  <span key={i} className="chip">
                                    {s}
                                  </span>
                                ))}
                              </div>
                            )}

                            <div className="flex items-center gap-3 text-xs text-glass-500">
                              <span className="tabular-nums">
                                Joined {formatDistanceToNow(new Date(toEpochMs(member.joined_at)), { addSuffix: true })}
                              </span>
                              {member.notes && (
                                <span className="truncate max-w-[200px]">{member.notes}</span>
                              )}
                            </div>
                          </div>

                          {canAdmin && (
                            <div className={cn(
                              'flex items-center gap-0.5 flex-shrink-0 transition-opacity',
                              !isMobile && 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'
                            )}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingMember(member);
                                }}
                                className="btn-icon touch-manipulation"
                                title="Edit member"
                                aria-label="Edit member"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteMember(member);
                                }}
                                className="btn-icon text-red-400 hover:text-red-300 touch-manipulation"
                                title="Remove member"
                                aria-label="Remove member"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                        <button data-tour="builder-contributions" type="button" aria-expanded={expandedMember === member.id} aria-controls={`contributions-${member.id}`} className="glass-button-secondary mt-3 w-full sm:w-auto" onClick={() => setExpandedMember(expandedMember === member.id ? null : member.id)}>
                          <Briefcase className="w-4 h-4" />{expandedMember === member.id ? 'Hide contributions' : 'View contributions'}<ChevronDown className={cn('w-4 h-4 transition-transform', expandedMember === member.id && 'rotate-180')} />
                        </button>
                        {expandedMember === member.id && <div id={`contributions-${member.id}`}><BuilderContributions member={member} onDownload={handleDownloadSchematic} /></div>}
                      </motion.div>
                    );
                  })}
                </div>
              </AnimatePresence>

              {teamTotalPages > 1 && renderPagination(
                teamPage,
                teamTotalPages,
                teamTotal,
                () => setTeamPage(p => Math.max(1, p - 1)),
                () => setTeamPage(p => Math.min(teamTotalPages, p + 1)),
              )}
            </>
          )}
        </>
      )}

      {/* ── Schematics Mode ─────────────────────────────────────────── */}
      {viewMode === 'schematics' && (
        <>
          {showSchematicUpload && canCreate && (
            <div className="glass-card p-4 md:p-5 space-y-4">
              <SectionHeader
                title="Upload schematics or screenshots"
                icon={Upload}
                description="Files uploaded here join the shared library. To attach them to a project or build straight away, upload from that page instead."
              />
              <SchematicUploadForm
                onDone={async () => {
                  setShowSchematicUpload(false);
                  await loadSchematics();
                }}
                onCancel={() => setShowSchematicUpload(false)}
              />
            </div>
          )}

          {schematicsError && renderError(schematicsError)}

          {schematicsLoading && schematics.length === 0 && renderListSkeleton()}

          {!schematicsLoading && schematics.length === 0 ? (
            <EmptyState
              icon={debouncedSchematicsSearch ? Search : FileArchive}
              title={debouncedSchematicsSearch ? 'No schematics match your search' : 'No schematics yet'}
              description={
                debouncedSchematicsSearch
                  ? 'Try adjusting your search query.'
                  : canCreate
                    ? 'Upload a schematic (.schem, .schematic, .nbt, .litematic) or a screenshot and give it a title. Files uploaded on a project or build show up here too.'
                    : 'Schematics and screenshots will appear here once uploaded.'
              }
              action={
                !debouncedSchematicsSearch && canCreate && !showSchematicUpload ? (
                  <button onClick={() => setShowSchematicUpload(true)} className="glass-button">
                    <Upload className="w-4 h-4" />
                    Upload a file
                  </button>
                ) : undefined
              }
            />
          ) : (
            <>
              <AnimatePresence>
                <div className="space-y-3">
                  {schematics.map((schematic, index) => (
                    <motion.div
                      key={schematic.id}
                      layout
                      variants={listItemVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      transition={{ duration: 0.15, delay: index * 0.03 }}
                      className="glass-card p-4 group"
                    >
                      {editingSchematicId === schematic.id ? (
                        <SchematicEditForm
                          schematic={schematic}
                          onSaved={async () => {
                            setEditingSchematicId(null);
                            await loadSchematics();
                          }}
                          onCancel={() => setEditingSchematicId(null)}
                        />
                      ) : (
                      <div className="flex items-start gap-4">
                        <span className="icon-tile icon-tile-neutral">
                          <FileArchive className="w-4 h-4" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="text-sm font-semibold text-white truncate">
                              {schematicDisplayTitle(schematic)}
                            </h3>
                            <span className={cn('badge', (schematic.file_kind ?? 'schematic') === 'screenshot' ? 'badge-info' : 'badge-neutral')}>
                              {schematicKindLabel(schematic.file_kind)}
                            </span>
                            {schematic.version && (
                              <span className="badge badge-neutral">
                                v{schematic.version}
                              </span>
                            )}
                            <span className="chip tabular-nums">
                              {formatFileSize(schematic.size)}
                            </span>
                          </div>
                          {schematicShowsFilename(schematic) && (
                            <p className="font-mono text-xs text-glass-500 mb-1 truncate" title={schematic.filename}>
                              {schematic.filename}
                            </p>
                          )}
                          {(schematic.file_kind ?? 'schematic') === 'screenshot' && (
                            <div className="mb-2 max-w-md">
                              <SchematicScreenshotPreview schematicId={schematic.id} alt={schematicDisplayTitle(schematic)} />
                            </div>
                          )}
                          {schematic.description && (
                            <p className="text-xs text-glass-400 mb-1 line-clamp-2">
                              {schematic.description}
                            </p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-glass-500 tabular-nums">
                            {schematic.uploader_id && (
                              <span className="inline-flex items-center gap-1">
                                by <CreatorProfile creatorId={schematic.uploader_id} inline />
                              </span>
                            )}
                            <span>
                              {formatDistanceToNow(new Date(toEpochMs(schematic.created_at)), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <SchematicDeliveryAction schematic={schematic} canSend={canCreate} compact />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadSchematic(schematic);
                            }}
                            disabled={downloadingId === schematic.id}
                            className="btn-icon border-glass-200/10"
                            title={(schematic.file_kind ?? 'schematic') === 'screenshot' ? 'Save image' : 'Download schematic'}
                            aria-label={(schematic.file_kind ?? 'schematic') === 'screenshot' ? 'Save image' : 'Download schematic'}
                          >
                            {downloadingId === schematic.id ? (
                              <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </button>
                          {canCreate && (
                            <button
                              type="button"
                              onClick={() => setEditingSchematicId(schematic.id)}
                              className="btn-icon border-glass-200/10"
                              title="Edit title, version and description"
                              aria-label="Edit title, version and description"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteSchematicId(schematic.id)}
                              disabled={deletingSchematicId === schematic.id}
                              className="btn-icon border-glass-200/10 text-red-400 hover:text-red-300"
                              title="Delete file everywhere"
                              aria-label="Delete file everywhere"
                            >
                              {deletingSchematicId === schematic.id ? (
                                <Loader className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </AnimatePresence>

              {schematicsTotalPages > 1 && renderPagination(
                schematicsPage,
                schematicsTotalPages,
                schematicsTotal,
                () => setSchematicsPage(p => Math.max(1, p - 1)),
                () => setSchematicsPage(p => Math.min(schematicsTotalPages, p + 1)),
              )}
            </>
          )}
        </>
      )}

      {/* ── Modals ──────────────────────────────────────────────────── */}
      {isCreateProjectOpen && (
        <CreateProjectModal
          isOpen={isCreateProjectOpen}
          onClose={() => setIsCreateProjectOpen(false)}
          onSuccess={() => {
            setIsCreateProjectOpen(false);
            handleRefresh();
          }}
        />
      )}

      {isCreateBuildOpen && (
        <CreateBuildItemModal
          isOpen={isCreateBuildOpen}
          onClose={() => setIsCreateBuildOpen(false)}
          onSuccess={() => {
            setIsCreateBuildOpen(false);
            handleRefresh();
          }}
        />
      )}

      {confirmDeleteSchematicId && (
        <ConfirmationModal
          isOpen
          onClose={() => setConfirmDeleteSchematicId(null)}
          onConfirm={handleDeleteSchematic}
          title="Delete file"
          message="Delete this file for good, everywhere it is linked? This cannot be undone."
          confirmText="Delete"
          isLoading={!!deletingSchematicId}
        />
      )}

      {isCreateMemberOpen && (
        <CreateTeamMemberModal
          isOpen={isCreateMemberOpen}
          onClose={() => setIsCreateMemberOpen(false)}
          onSuccess={() => {
            setIsCreateMemberOpen(false);
            handleRefresh();
          }}
        />
      )}

      {editingMember && (
        <EditTeamMemberModal
          isOpen={!!editingMember}
          onClose={() => setEditingMember(null)}
          onSuccess={() => {
            setEditingMember(null);
            handleRefresh();
          }}
          member={editingMember}
        />
      )}

      {isTagManagementOpen && (
        <BuildTagManagementModal
          isOpen={isTagManagementOpen}
          onClose={() => {
            setIsTagManagementOpen(false);
            buildTeamApiService.listTags().then(setTags).catch(() => {});
          }}
        />
      )}

      {memberToDelete && (
        <ConfirmationModal
          isOpen={!!memberToDelete}
          onClose={() => setMemberToDelete(null)}
          onConfirm={confirmDeleteMember}
          title="Remove Team Member"
          message={`Are you sure you want to remove ${memberToDelete.display_name} from the build team? This action cannot be undone.`}
          confirmText="Remove"
          isLoading={isDeletingMember}
        />
      )}
    </div>
  );
};
